import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./style.scss";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllTradersRequest,
  sendCredential,
  updateUserAccountReq,
  addBalanceRequest,
} from "../../../utils/api/apis";
import { setIsLoading } from "../../../store/reducers/authSlice";
import { toast, ToastContainer } from "react-toastify";
import { ReactComponent as SearchNew } from "../../../assets/icons/search-svgrepo-com (1).svg";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import CopyToClipboard from "react-copy-to-clipboard";
import moment from "moment/moment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { returnErrors } from "../../../store/reducers/error";
import { returnMessages } from "../../../store/reducers/message";
import ConfirmationModal from "../../../pages/FundingEvaluation/ConfirmationModal";
import BalanceModal from "./BalanceModal";
import { formatText, statusClassMap } from "../../../utils/string";
import { ReactComponent as CopyIcon } from "../../../assets/icons/CopyIcon.svg";
import { ReactComponent as BrandCopyIcon } from "../../../assets/icons/BrandCopyIcon.svg";
import Select from "react-select";
import {
  setActiveAccount,
  setActiveUser,
} from "../../../store/reducers/accountSlice";
// Import your reusable table component
import ReactTable from "../../../reusableComponents/ReactTable/ReactTable";

// Main Component
function AllTable({ activeTab, admin }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const idToken = useSelector((state) => state.auth.idToken);
  const { selectedBrand } = useSelector((state) => state.brand);

  const [params, setParams] = useState({
    page: 1,
    page_size: 20,
    active: null,
    search: null,
    type: null,
    platform: null,
    phase: null,
  });

  const [allTraders, setAllTraders] = useState([]);
  const [totalTraders, setTotalTraders] = useState(0);

  // --- 1. Data Fetching Logic ---
  useEffect(() => {
    const tabMapping = {
      0: null,
      1: "1_step",
      2: "2_step",
      3: "1_step",
      4: "2_step",
      5:
        selectedBrand?.brand === "equity_edge" ? "pro_edge" : "instant_funding",
    };

    setParams((prev) => ({
      ...prev,
      type: tabMapping[activeTab],
      page: 1,
      phase: activeTab === 3 || activeTab === 4 ? "funded" : null,
    }));
  }, [activeTab, selectedBrand]);

  const fetchTraders = async () => {
    dispatch(setIsLoading(true));
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== null),
    );
    try {
      const data = await getAllTradersRequest(
        idToken,
        filteredParams,
        selectedBrand,
      );
      setAllTraders(data.results);
      setTotalTraders(data.count);
    } catch (error) {
      console.error("Error fetching traders:", error);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  useEffect(() => {
    fetchTraders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const searchValue = e.target.querySelector("input").value;
    setParams((prev) => ({ ...prev, search: searchValue, page: 1 }));
  };

  // --- 2. Action Handlers ---
  const handleCopyToClipboard = () => {
    toast("Copied email", {
      position: "top-right",
      autoClose: 2000,
      type: "success",
      theme: "dark",
    });
  };

  const handleActiveAccount = (row) => {
    row.original?.login_id
      ? dispatch(setActiveAccount(row.original))
      : dispatch(setActiveAccount(null));
    dispatch(setActiveUser({ ...row.original }));
  };

  const handleSendCredential = async (row) => {
    const login_id = {
      login_id: row.original.login_id,
      trading_platform: row.original.trading_platform,
      email: row.original.email,
    };
    const response = await sendCredential(idToken, login_id);
    if (response?.status < 399) {
      dispatch(returnMessages(response?.data?.detail, response?.status));
    } else {
      const msg = response?.response?.data?.detail || "Something went wrong";
      dispatch(returnErrors(msg, 400));
    }
  };

  const handleUpdateConfirm = async (row, action) => {
    try {
      const response = await updateUserAccountReq(idToken, {
        login_id: row.original.login_id,
        type: action,
        trading_platform: row.original.trading_platform,
        email: row.original.email,
      });
      if (response?.status < 399) {
        dispatch(
          returnMessages(`Account ${action}d successfully`, response?.status),
        );
        fetchTraders();
      } else {
        const msg =
          response?.response?.data?.detail || "Failed to update account";
        dispatch(returnErrors(msg, 400));
      }
    } catch (error) {
      dispatch(returnErrors("Error updating account", 500));
    }
  };

  // --- 3. Sub Component for Audit Logs ---
  const renderRowSubComponent = useCallback(({ row }) => {
    const logs = row.original.account_audit_logs || [];

    if (logs.length === 0) {
      return (
        <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
          No audit logs found for this account.
        </div>
      );
    }

    return (
      <div style={{ padding: "20px", background: "#f9f9f9" }}>
        <table
          className="audit-table"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr
              className="logs_header_tr"
              style={{ textAlign: "left", background: "#e0e0e0" }}
            >
              <th style={{ padding: "8px" }}>Field</th>
              <th style={{ padding: "8px" }}>Old Value</th>
              <th style={{ padding: "8px" }}>New Value</th>
              <th style={{ padding: "8px" }}>Changed By</th>
              <th style={{ padding: "8px" }}>IP Address</th>
              <th style={{ padding: "8px" }}>Reason</th>
              <th style={{ padding: "8px" }}>Changed At</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, index) => (
              <tr
                key={index}
                className="logs_tr"
                style={{ borderBottom: "1px solid #ddd" }}
              >
                <td style={{ padding: "8px" }}>
                  {log.field_name?.replace("_", " ") || "-"}
                </td>
                <td style={{ padding: "8px" }}>
                  {log.old_value?.replace("_", " ") || "-"}
                </td>
                <td style={{ padding: "8px" }}>{log.new_value || "-"}</td>
                <td style={{ padding: "8px" }}>{log.changed_by || "-"}</td>
                <td style={{ padding: "8px" }}>{log.ip_address || "-"}</td>
                <td className="reason-column" style={{ padding: "8px" }}>
                  {log.change_reason || "-"}
                </td>
                <td style={{ padding: "8px" }}>
                  {moment(log.changed_at).format("YYYY-MM-DD HH:mm:ss")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, []);

  // --- 4. Column Definitions ---
  const columns = useMemo(
    () => [
      {
        Header: () => <div className="column_header">Email</div>,
        accessor: "Name",
        Cell: ({ row }) => {
          const randomColor = useMemo(
            () => `#${Math.floor(Math.random() * 16777215).toString(16)}`,
            [],
          );

          const handleNavigation = () => {
            handleActiveAccount(row);
            navigate("/traders-list-2");
          };

          return (
            <div onClick={handleNavigation}>
              <div className="column_one_wrapper">
                <div
                  className="sno_wrapper"
                  style={{ backgroundColor: randomColor }}
                >
                  {row.original?.email?.charAt(0)}
                </div>
                <div>{row.original?.email}</div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <CopyToClipboard
                    onCopy={handleCopyToClipboard}
                    text={row.original?.email}
                  >
                    <p
                      className="table_copy_button"
                      style={{ marginRight: "20px", cursor: "pointer" }}
                    >
                      {selectedBrand?.brand === "crypto_edge" ? (
                        <BrandCopyIcon />
                      ) : (
                        <CopyIcon />
                      )}
                    </p>
                  </CopyToClipboard>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        Header: "Login ID",
        accessor: "login_id",
        Cell: ({ row }) => (
          <Link to="/traders-list-2" onClick={() => handleActiveAccount(row)}>
            {row.original.login_id}
          </Link>
        ),
      },
      {
        Header: "Start Date",
        accessor: "start_date",
        Cell: ({ row }) =>
          moment.utc(row.original.start_date).format("DD-MM-YYYY"),
      },
      {
        Header: "Platform",
        accessor: "trading_platform",
        Cell: ({ row }) => (
          <span style={{ textTransform: "capitalize" }}>
            {row.original.trading_platform}
          </span>
        ),
      },
      {
        Header: "Challenge",
        accessor: "challenge",
        Cell: ({ row }) => <span> {row.original?.challenge}</span>,
      },
      {
        Header: "Challenge Type",
        accessor: "plan_name",
        Cell: ({ row }) => <span> {row.original?.plan_name}</span>,
      },
      {
        Header: "Phase",
        accessor: "phase",
        Cell: ({ row }) => <span> {formatText(row.original?.phase)}</span>,
      },
      {
        Header: () => <div className="column_header">Status</div>,
        accessor: "is_active",
        Cell: ({ row, value }) => (
          <Link onClick={() => handleActiveAccount(row)} to="/traders-list-2">
            <div
              className={`status_wrapper ${
                value ? statusClassMap.succeeded : "Inactive_tag"
              }`}
            >
              {row.original.is_active ? "Active" : "Disabled"}
            </div>
          </Link>
        ),
      },
      {
        Header: "Issue Type",
        accessor: "issue_type",
        Cell: ({ value }) => (
          <p style={{ textTransform: "capitalize" }}>{value ? value : "-"}</p>
        ),
      },
      {
        Header: "Balance Update",
        accessor: "balance_update",
        Cell: ({ row }) => {
          const [balanceModal, setBalanceModal] = useState(false);

          const handleBalanceConfirm = async (formData) => {
            const payload = {
              login_id: formData.login_id,
              balance: formData.balance,
              email: formData.email,
            };
            const response = await addBalanceRequest(idToken, payload);
            if (response?.status < 399) {
              dispatch(
                returnMessages("Balance added successfully", response?.status),
              );
              fetchTraders();
            } else {
              const msg =
                response?.response?.data?.detail ||
                response?.data?.detail ||
                "Failed to add balance";
              dispatch(returnErrors(msg, 400));
            }
          };

          return (
            <div onClick={(e) => e.stopPropagation()}>
              <button
                className="upgrade_button"
                onClick={() => setBalanceModal(true)}
                style={{ padding: "5px 10px", fontSize: "12px" }}
              >
                Add balance
              </button>
              {balanceModal && (
                <BalanceModal
                  initialData={{
                    login_id: row.original.login_id,
                    email: row.original.email,
                  }}
                  setBalanceModal={setBalanceModal}
                  onConfirm={handleBalanceConfirm}
                />
              )}
            </div>
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => {
          const [anchorEl, setAnchorEl] = useState(null);
          const [confirmationModal, setConfirmationModal] = useState(false);
          const open = Boolean(anchorEl);

          const handleClick = (event) => setAnchorEl(event.currentTarget);
          const handleClose = () => setAnchorEl(null);

          const handleStatus = () => {
            setConfirmationModal(true);
            handleClose();
          };

          return (
            <div onClick={(e) => e.stopPropagation()}>
              {confirmationModal && (
                <ConfirmationModal
                  id={row.original.login_id}
                  setConfirmationModal={setConfirmationModal}
                  onConfirm={() => {
                    const actionType = row.original.is_active
                      ? "disable"
                      : "reinstantiate";
                    handleUpdateConfirm(row, actionType);
                  }}
                />
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <IconButton onClick={handleClick} size="small" sx={{ ml: 2 }}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    minWidth: "150px",
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                    mt: 1.5,
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={() => handleSendCredential(row)}>
                  Send Credential
                </MenuItem>
                <MenuItem onClick={handleStatus}>
                  {row.original.is_active ? "Disable" : "Activate"}
                </MenuItem>
              </Menu>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedBrand],
  );

  const plantypesOptions = [
    { label: "All", value: null },
    { label: "Level 1", value: "Level 1" },
    { label: "Level 2", value: "Level 2" },
    { label: "Level 3", value: "Level 3" },
    { label: "VIP Level", value: "VIP Level" },
  ];

  const transformedData = useMemo(() => {
    return allTraders.map((trader) => ({
      ...trader,
      user_audit_logs: trader.account_audit_logs,
    }));
  }, [allTraders]);

  return (
    <div className="alltable">
      <ToastContainer />
      <div className="alltable_header">
        <form className="search_container" onSubmit={handleSearchSubmit}>
          <input type="text" placeholder="Search..." />
          <button type="submit" style={{ padding: "3px 10px 0 0" }}>
            <SearchNew style={{ width: "100%" }} />
          </button>
        </form>
        <div className="sort_buttons">
          <button
            onClick={() =>
              setParams((prev) => ({
                ...prev,
                active: prev.active === "True" ? null : "True",
                page: 1,
              }))
            }
            className={params.active === "True" ? "activeBtn" : ""}
          >
            Active
          </button>
          <button
            onClick={() =>
              setParams((prev) => ({
                ...prev,
                active: prev.active === "False" ? null : "False",
                page: 1,
              }))
            }
            className={params.active === "False" ? "activeBtn" : ""}
          >
            Inactive
          </button>
          {selectedBrand?.brand === "equity_edge" && (
            <>
              <button
                onClick={() =>
                  setParams((pre) => ({ ...pre, platform: "matchtrade" }))
                }
                className={params?.platform === "matchtrade" ? "activeBtn" : ""}
              >
                Matchtrade
              </button>
              <button
                onClick={() =>
                  setParams((pre) => ({ ...pre, platform: "tradelocker" }))
                }
                className={
                  params?.platform === "tradelocker" ? "activeBtn" : ""
                }
              >
                Tradelocker
              </button>
              <button
                onClick={() =>
                  setParams((pre) => ({ ...pre, platform: "mt5" }))
                }
                className={params?.platform === "mt5" ? "activeBtn" : ""}
              >
                MT5
              </button>
            </>
          )}
        </div>
        <div className="input_form-mid">
          <div className="input_wrapper">
            <Select
              isSearchable={false}
              options={plantypesOptions}
              value={plantypesOptions.find(
                (item) => item.value === params.scaling_account,
              )}
              onChange={(options) =>
                setParams((pre) => ({ ...pre, scaling_account: options.value }))
              }
            />
          </div>
        </div>
      </div>

      <ReactTable
        columns={columns}
        data={transformedData}
        params={params}
        setParams={setParams}
        serverSide={true}
        totalCount={totalTraders}
        renderRowSubComponent={renderRowSubComponent}
      />
    </div>
  );
}

export default AllTable;
